import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D40fromD1 = () => {
  const title = "Calculation of D40 (Khavedamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D40 chart, or Khavedamsa chart, is used in Vedic astrology to
        analyze inherited traits, maternal lineage, and obstacles or challenges
        that arise from the mother's side. Below is the step-by-step process to
        calculate the D40 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D40 (Khavedamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Saturn is located at 15° Capricorn, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Forty Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into forty equal parts, each spanning 0.75°.
        To find the Khavedamsa position of a planet, we divide its degree in the
        sign by 0.75.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Khavedamsa \ Position = \frac{Degree \ in \ Sign}{0.75}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{0.75} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 0.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{0.75} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 0.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{0.75} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 0.75°</strong>.
        </Typography>
      </Box>

      {/* Add similar blocks for all 40 parts */}

      <Typography variant="h5" gutterBottom>
        3. Assign the Khavedamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 0.75°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 0.75°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 0.75°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 0.75°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 0.75°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 0.75°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 0.75°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 0.75°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Ninth 0.75°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Tenth 0.75°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Saturn at 15° Capricorn
      </Typography>

      <Typography variant="body1" paragraph>
        Capricorn is the D1 sign. Saturn at 15° falls in the twentieth 0.75°
        (between 15° and 15.75°). Therefore, it belongs to the{" "}
        <strong>20th sign</strong> from Capricorn, which is <strong>Leo</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D40 (Khavedamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D40
        (Khavedamsa) position from the D1 (Rasi) chart, using the planet's
        degree and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_khavedamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Khavedamsa the planet is in based on its degree
    if degree <= 0.75:
        # First 0.75° belongs to the same sign
        khavedamsa_sign = zodiac_signs[sign_index]
    elif 0.75 < degree <= 1.5:
        # Second 0.75° belongs to the 2nd sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 1.5 < degree <= 2.25:
        # Third 0.75° belongs to the 3rd sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 2.25 < degree <= 3:
        # Fourth 0.75° belongs to the 4th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 3 < degree <= 3.75:
        # Fifth 0.75° belongs to the 5th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 3.75 < degree <= 4.5:
        # Sixth 0.75° belongs to the 6th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 4.5 < degree <= 5.25:
        # Seventh 0.75° belongs to the 7th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 5.25 < degree <= 6:
        # Eighth 0.75° belongs to the 8th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 6 < degree <= 6.75:
        # Ninth 0.75° belongs to the 9th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 6.75 < degree <= 7.5:
        # Tenth 0.75° belongs to the 10th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 7.5 < degree <= 8.25:
        # Eleventh 0.75° belongs to the 11th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 8.25 < degree <= 9:
        # Twelfth 0.75° belongs to the 12th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 9 < degree <= 9.75:
        # Thirteenth 0.75° belongs to the 13th sign from the current sign
        khavedamsa_sign = zodiac_signs[sign_index]
    elif 9.75 < degree <= 10.5:
        # Fourteenth 0.75° belongs to the 14th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 10.5 < degree <= 11.25:
        # Fifteenth 0.75° belongs to the 15th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 11.25 < degree <= 12:
        # Sixteenth 0.75° belongs to the 16th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 12 < degree <= 12.75:
        # Seventeenth 0.75° belongs to the 17th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 12.75 < degree <= 13.5:
        # Eighteenth 0.75° belongs to the 18th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 13.5 < degree <= 14.25:
        # Nineteenth 0.75° belongs to the 19th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 14.25 < degree <= 15:
        # Twentieth 0.75° belongs to the 20th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 15 < degree <= 15.75:
        # Twenty-first 0.75° belongs to the 21st sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 15.75 < degree <= 16.5:
        # Twenty-second 0.75° belongs to the 22nd sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 16.5 < degree <= 17.25:
        # Twenty-third 0.75° belongs to the 23rd sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 17.25 < degree <= 18:
        # Twenty-fourth 0.75° belongs to the 24th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 18 < degree <= 18.75:
        # Twenty-fifth 0.75° belongs to the 25th sign from the current sign
        khavedamsa_sign = zodiac_signs[sign_index]
    elif 18.75 < degree <= 19.5:
        # Twenty-sixth 0.75° belongs to the 26th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 19.5 < degree <= 20.25:
        # Twenty-seventh 0.75° belongs to the 27th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 20.25 < degree <= 21:
        # Twenty-eighth 0.75° belongs to the 28th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 21 < degree <= 21.75:
        # Twenty-ninth 0.75° belongs to the 29th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 21.75 < degree <= 22.5:
        # Thirtieth 0.75° belongs to the 30th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 22.5 < degree <= 23.25:
        # Thirty-first 0.75° belongs to the 31st sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 23.25 < degree <= 24:
        # Thirty-second 0.75° belongs to the 32nd sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 24 < degree <= 24.75:
        # Thirty-third 0.75° belongs to the 33rd sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 24.75 < degree <= 25.5:
        # Thirty-fourth 0.75° belongs to the 34th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 25.5 < degree <= 26.25:
        # Thirty-fifth 0.75° belongs to the 35th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 26.25 < degree <= 27:
        # Thirty-sixth 0.75° belongs to the 36th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 27 < degree <= 27.75:
        # Thirty-seventh 0.75° belongs to the 37th sign from the current sign
        khavedamsa_sign = zodiac_signs[sign_index]
    elif 27.75 < degree <= 28.5:
        # Thirty-eighth 0.75° belongs to the 38th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 28.5 < degree <= 29.25:
        # Thirty-ninth 0.75° belongs to the 39th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    else:
        # Fortieth 0.75° belongs to the 40th sign from the current sign
        khavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]

    return khavedamsa_sign

# Example usage
planet = "Saturn"
degree = 15
sign = "Capricorn"

khavedamsa_sign = calculate_khavedamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {khavedamsa_sign} in the D40 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Khavedamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        forty equal parts and assigns the Khavedamsa based on the planet's
        degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into forty parts: the first 0.75°, the
        second 0.75°, and so on. It assigns the Khavedamsa based on the planet's
        degree within the sign:
        <br />
        - First 0.75° belongs to the same sign.
        <br />
        - Second 0.75° belongs to the 2nd sign from the D1 sign.
        <br />
        - Third 0.75° belongs to the 3rd sign from the D1 sign.
        <br />
        - Fourth 0.75° belongs to the 4th sign from the D1 sign.
        <br />
        - Fifth 0.75° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 0.75° belongs to the 6th sign from the D1 sign.
        <br />
        - Seventh 0.75° belongs to the 7th sign from the D1 sign.
        <br />
        - Eighth 0.75° belongs to the 8th sign from the D1 sign.
        <br />
        - Ninth 0.75° belongs to the 9th sign from the D1 sign.
        <br />- Tenth 0.75° belongs to the 10th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D40fromD1;
